import { Suspense, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { useQueryCRUD, useRunQuery } from '@hooks/useQuery'
import { hasBlueprintChanged } from 'src/utils/blueprintUtils'
import { useAppSelector, useAppDispatch } from 'universal-scripts'
import { updateOriginalToDraft } from 'src/store/slices/query'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage'
import Loader from '@components/Loader/Loader'
import { BuilderAreaNew, BuilderAreaExisting } from './BuilderArea/BuilderArea'
import type { QueryListItem } from 'src/types/queries'
import NodeEditor from './NodeEditor/NodeEditor'
import { useUnsavedChangesWarning } from '@hooks/useUnsavedChangesWarning'
import Toast from '@components/Toast/Toast'
import NodeViewer from './NodeViewer/NodeViewer'
import { useTrialGuard } from '@hooks/useTrialGuard'

export default function EditLayout({ id }: { id: string }) {
  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { guard } = useTrialGuard()

  const { createQuery, updateQuery } = useQueryCRUD()
  const { runQuery } = useRunQuery()
  const [isRunning, setIsRunning] = useState(false)
  const [toastMessage, setToastMessage] = useState<string | null>(null)

  const { draft, original } = useAppSelector((state) => state.query)

  const isNewQuery = id === 'new'

  const hasChanges = useMemo(() => {
    if (!original) {
      return (draft.blueprint?.length ?? 0) > 0
    }

    if (draft.title !== original.title) return true
    if (draft.public !== original.public) return true
    if (hasBlueprintChanged(original.blueprint, draft.blueprint)) return true

    return false
  }, [draft, original])

  const hasTitle = (draft.title ?? '').trim() !== ''

  const canRun = useMemo(() => {
    const hasBlueprint = draft.blueprint && draft.blueprint.length === 1
    const isSavedInDB = !isNewQuery

    return hasBlueprint && isSavedInDB && !hasChanges
  }, [draft.blueprint, isNewQuery, hasChanges])

  useUnsavedChangesWarning(hasChanges)

  const handleRun = async () => {
    setIsRunning(true)
    try {
      await runQuery(id)
      setToastMessage(intl.formatMessage({ id: 'builder.alert.executed' }))
    } catch (error) {
      console.error('run:', error)
    } finally {
      setIsRunning(false)
    }
  }

  const handleSave = async () => {
    if (!hasTitle) {
      setToastMessage(intl.formatMessage({ id: 'builder.alert.no-title' }))
      return
    }
    try {
      if (isNewQuery) {
        const response = await createQuery({
          title: draft.title,
          blueprint: draft.blueprint,
          public: draft.public
        })

        if (response?.ok && response?.query?.id) {
          dispatch(updateOriginalToDraft())
          navigate(`/query-builder/${response.query.id}`, { replace: true })
        }
      } else {
        const payload: Partial<QueryListItem> = {}
        if (!original || draft.title !== original.title) {
          payload.title = draft.title
        }
        if (!original || draft.public !== original.public) {
          payload.public = draft.public
        }
        if (hasBlueprintChanged(original?.blueprint, draft.blueprint)) {
          payload.blueprint = draft.blueprint
        }
        if (Object.keys(payload).length > 0) {
          await updateQuery(id, payload)
          dispatch(updateOriginalToDraft())
          setToastMessage(intl.formatMessage({ id: 'builder.alert.saved' }))
        }
      }
    } catch (error) {
      console.error('save:', error)
    }
  }

  return (
    <>
      <section id="builder-content" className="main-content">
        <div className="query-actions">
          <button
            className="highlight-button"
            disabled={!canRun || isRunning}
            onClick={guard(handleRun)}
          >
            <span className="icon-play-stroke" />
            <span>
              <FormattedMessage id="run" />
            </span>
          </button>
          <button
            className="standart-button"
            onClick={guard(handleSave)}
            disabled={!hasChanges}
          >
            <span className="icon icon-diskette" />
            <span>
              <FormattedMessage id="save" />
            </span>
          </button>
        </div>
        <ErrorBoundary errorComponent={ErrorMessage}>
          <Suspense fallback={<Loader />}>
            {isNewQuery ? <BuilderAreaNew /> : <BuilderAreaExisting id={id} />}
          </Suspense>
        </ErrorBoundary>
        <NodeEditor />
        <NodeViewer />
      </section>
      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => {
            setToastMessage(null)
          }}
        />
      )}
    </>
  )
}
